import React, { useState, useEffect } from 'react';
import LandingPage from './views/LandingPage';
import RegistrationPage from './views/RegistrationPage';
import PaymentPage from './views/PaymentPage';
import SuccessPage from './views/SuccessPage';
import AdminPanel from './views/AdminPanel';
import AdminLogin from './views/AdminLogin';
import Navbar from './components/Navbar';
import Logo from './components/Logo';
import { View, Registration } from './types';

// Helper seguro para Storage (evita erro em modo anônimo)
const safeStorage = {
  getItem: (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  },
  removeItem: (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {}
  }
};

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<View>('home');

  const [currentReg, setCurrentReg] = useState<Registration | null>(() => {
    const saved = safeStorage.getItem('currentRegistration');
    return saved ? JSON.parse(saved) : null;
  });

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => {
    return safeStorage.getItem('isAdminAuth') === 'true';
  });

  useEffect(() => {
    if (currentReg) {
      safeStorage.setItem('currentRegistration', JSON.stringify(currentReg));
    } else {
      safeStorage.removeItem('currentRegistration');
    }
  }, [currentReg]);

  const navigate = (view: View, data?: Registration) => {
    if (data) setCurrentReg(data);

    if (view === 'admin-login' && isAdminAuthenticated) {
      setCurrentView('admin');
    } else {
      setCurrentView(view);
    }

    try {
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch (e) {}

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAdminAuth = (success: boolean) => {
    if (success) {
      setIsAdminAuthenticated(true);
      safeStorage.setItem('isAdminAuth', 'true');
      setCurrentView('admin');
    }
  };

  const renderView = () => {
    switch (currentView) {
      case 'home':
      case 'admin-login':
        return <LandingPage onStartRegistration={() => navigate('registration')} />;
      case 'registration':
        return <RegistrationPage onRegistered={(reg) => navigate('payment', reg)} />;
      case 'payment':
        return currentReg ? (
          <PaymentPage
            registration={currentReg}
            onPaymentConfirmed={(updated) => navigate('success', updated || currentReg)}
          />
        ) : <LandingPage onStartRegistration={() => navigate('registration')} />;
      case 'success':
        return <SuccessPage onBackHome={() => {
          setCurrentReg(null);
          navigate('home');
        }} registration={currentReg} />;
      case 'admin':
        return isAdminAuthenticated ? <AdminPanel /> : <AdminLogin onAuth={handleAdminAuth} onCancel={() => navigate('home')} />;
      default:
        return <LandingPage onStartRegistration={() => navigate('registration')} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-brand-paper text-gray-900 selection:bg-brand-amber selection:text-white">
      <Navbar currentView={currentView} onNavigate={navigate} />

      <main className="flex-grow relative">
        {renderView()}

        {currentView === 'admin-login' && (
          <AdminLogin
            onAuth={handleAdminAuth}
            onCancel={() => navigate('home')}
          />
        )}
      </main>

      <footer className="bg-brand-night text-white border-t border-white/5 py-16 px-4 print:hidden">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12">
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
              <Logo className="w-8 h-8 rounded-lg" />
              <h3 className="font-display text-xl font-semibold uppercase tracking-wide">IPCC</h3>
            </div>
            <p className="text-white/40 text-sm max-w-xs leading-relaxed">
              Igreja Presbiteriana Central de Cascavel. Homens firmes na fé, edificados na Palavra e centrados em Cristo.
            </p>
          </div>

          <div className="flex flex-col items-center gap-2">
            <p className="text-brand-amber text-[10px] font-black tracking-[0.3em] uppercase">Congresso de Homens 2026</p>
            <p className="text-white/30 text-[10px] text-center">© IPCC — Igreja Presbiteriana Central de Cascavel.<br />Todos os direitos reservados.</p>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => navigate('admin-login')}
              className="px-6 py-3 border border-white/10 text-white/40 rounded-2xl text-[10px] font-black tracking-widest uppercase hover:text-brand-amber hover:border-brand-amber transition-all duration-300"
            >
              Área Administrativa
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
